import * as Sentry from '@sentry/nuxt';
import {
  isInjectedWebVitalsStartTimeError,
  isNuxtBuildMetaFetchError,
} from '~/utils/ignoredMonitoringErrors';
import { recoverFromNuxtChunkLoadError } from '~/utils/nuxtChunkLoadRecovery';
import { reportClientMonitoringError } from '~/utils/telegramErrorMonitor';

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter();
  const route = useRoute();
  const runtimeConfig = useRuntimeConfig();
  const sentryConfig = (runtimeConfig.public.sentry ?? {}) as Record<
    string,
    unknown
  >;
  const attachEmail = String(
    sentryConfig.attachUserEmail ?? sentryConfig.sendPii ?? 'false',
  ).toLowerCase();
  const shouldAttachEmail = ['1', 'true', 'yes', 'on'].includes(attachEmail);
  const { data: authData, status } = useAuth();

  const syncUserContext = () => {
    if (status.value !== 'authenticated') {
      Sentry.setUser(null);
      Sentry.setContext('company', null);
      return;
    }

    const sessionUser = (authData.value as any)?.user;
    const userData = sessionUser?.data ?? sessionUser ?? {};
    const contact = userData?.contact ?? {};
    const company = contact?.company ?? userData?.company ?? {};
    const userId = userData?.userId ?? userData?.id ?? sessionUser?.id;
    const email = userData?.email ?? sessionUser?.email;
    const companyId =
      contact?.company_id ??
      contact?.companyId ??
      company?.id ??
      userData?.company_id ??
      userData?.companyId;

    if (userId || email) {
      Sentry.setUser({
        id: userId ? String(userId) : undefined,
        email:
          shouldAttachEmail && typeof email === 'string' && email !== ''
            ? email
            : undefined,
      });
    }

    if (companyId || company?.name) {
      Sentry.setContext('company', {
        id: companyId ?? undefined,
        name: stringifyLocalizedName(company?.name),
      });
    }
  };

  const syncRouteContext = () => {
    Sentry.setTag('route', route.fullPath);
    Sentry.setContext('route', {
      name: route.name ? String(route.name) : undefined,
      path: route.path,
      fullPath: route.fullPath,
    });
  };

  watch(
    [() => status.value, () => authData.value],
    () => {
      syncUserContext();
      syncRouteContext();
    },
    { immediate: true },
  );

  router.afterEach((to, from, failure) => {
    Sentry.addBreadcrumb({
      category: 'navigation',
      type: 'navigation',
      level: failure ? 'warning' : 'info',
      message: `${from.fullPath || '(start)'} -> ${to.fullPath}`,
      data: {
        from: from.fullPath,
        to: to.fullPath,
        routeName: to.name ? String(to.name) : undefined,
      },
    });

    Sentry.setTag('route', to.fullPath);
    Sentry.setContext('route', {
      name: to.name ? String(to.name) : undefined,
      path: to.path,
      fullPath: to.fullPath,
    });
  });

  router.onError((error, to, from) => {
    if (
      recoverFromNuxtChunkLoadError(error, {
        source: 'router.navigation',
        from: from.fullPath,
        to: to.fullPath,
        routeName: to.name ? String(to.name) : undefined,
      })
    ) {
      return;
    }

    captureMonitoringException(error, 'router.navigation', {
      from: from.fullPath,
      to: to.fullPath,
      routeName: to.name ? String(to.name) : undefined,
    });
  });

  nuxtApp.hook('vue:error', (error, _instance, info) => {
    if (
      recoverFromNuxtChunkLoadError(error, {
        source: 'vue.error',
        info,
      })
    ) {
      return;
    }

    captureMonitoringException(error, 'vue.error', { info });
  });

  nuxtApp.hook('app:error', (error) => {
    if (recoverFromNuxtChunkLoadError(error, { source: 'nuxt.app.error' })) {
      return;
    }

    captureMonitoringException(error, 'nuxt.app.error');
  });

  window.addEventListener('error', (event) => {
    const error = event.error ?? new Error(event.message);

    if (
      recoverFromNuxtChunkLoadError(error, {
        source: 'window.error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      })
    ) {
      event.preventDefault();
      return;
    }

    captureMonitoringException(error, 'window.error', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason ?? 'Unhandled promise rejection'));

    if (isNuxtBuildMetaFetchError(reason)) {
      event.preventDefault();
      return;
    }

    if (
      recoverFromNuxtChunkLoadError(reason, {
        source: 'window.unhandledrejection',
      })
    ) {
      event.preventDefault();
      return;
    }

    captureMonitoringException(reason, 'window.unhandledrejection');
  });
});

const captureMonitoringException = (
  error: unknown,
  source: string,
  context: Record<string, unknown> = {},
) => {
  if (
    isInjectedWebVitalsStartTimeError(error, context) ||
    isNuxtBuildMetaFetchError(error, context)
  ) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('monitoring.source', source);
    scope.setContext('monitoring', context);
    Sentry.captureException(error);
  });

  void reportClientMonitoringError(error, source, context);
};

const stringifyLocalizedName = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;

    return typeof record.en === 'string'
      ? record.en
      : typeof record.ar === 'string'
        ? record.ar
        : undefined;
  }

  return undefined;
};
